// The application menu bar: File / Edit / Selection / View / Go / Run / Help,
// the way a real desktop IDE presents its commands. Each menu opens a dropdown
// of real actions wired to the store (file ops, view toggles, compile) and to
// the focused Monaco editor (undo/redo/cut/copy/paste/find/go-to-line) via the
// editorHandle singleton. Classic menu-bar behaviour: click to open, hover to
// switch between open menus, click-away or Esc to close, arrow keys to move.
//
// Parameterless: binds to the store. Lives inside the title bar.

import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { Check } from "lucide-react";
import { useIde } from "../store";
import { NEW_CONTRACT_TEMPLATE } from "../fs/examples";
import { runEditorCommand } from "../editor/editorHandle";
import { THEMES } from "../theme";

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
const MOD = isMac ? "⌘" : "Ctrl";
const ALT = isMac ? "⌥" : "Alt";

type MenuItem =
  | { kind: "sep" }
  | { kind: "head"; label: string }
  | {
      kind: "item";
      label: string;
      hint?: string;
      checked?: boolean;
      disabled?: boolean;
      run: () => void;
    };

interface Menu {
  id: string;
  label: string;
  items: MenuItem[];
}

function uniqueUntitled(files: Record<string, unknown>): string {
  if (!files["untitled.sl"]) return "untitled.sl";
  for (let n = 1; ; n++) {
    const p = `untitled-${n}.sl`;
    if (!files[p]) return p;
  }
}

export function MenuBar() {
  const [open, setOpen] = useState<string | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  const files = useIde((s) => s.files);
  const activePath = useIde((s) => s.activePath);
  const theme = useIde((s) => s.prefs.theme);
  const sidebarVisible = useIde((s) => s.sidebarVisible);
  const bottomVisible = useIde((s) => s.bottomVisible);

  const newFile = useIde((s) => s.newFile);
  const closeTab = useIde((s) => s.closeTab);
  const runCompile = useIde((s) => s.runCompile);
  const setCommandPalette = useIde((s) => s.setCommandPalette);
  const toggleSidebar = useIde((s) => s.toggleSidebar);
  const toggleBottom = useIde((s) => s.toggleBottom);
  const setBottomTab = useIde((s) => s.setBottomTab);
  const setActivityView = useIde((s) => s.setActivityView);
  const setTheme = useIde((s) => s.setTheme);

  // Close on outside click and on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) setOpen(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(null);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const run = useCallback((fn: () => void) => {
    setOpen(null);
    // Defer so the menu unmounts before the action (matters for editor focus).
    requestAnimationFrame(fn);
  }, []);

  const newContract = () => newFile(uniqueUntitled(files), NEW_CONTRACT_TEMPLATE);

  const menus: Menu[] = [
    {
      id: "file",
      label: "File",
      items: [
        { kind: "item", label: "New Contract", run: newContract },
        {
          kind: "item",
          label: "New File",
          run: () => newFile(uniqueUntitled(files)),
        },
        { kind: "sep" },
        {
          kind: "item",
          label: "Go to File…",
          hint: `${MOD}P`,
          run: () => setCommandPalette(true),
        },
        { kind: "sep" },
        {
          kind: "item",
          label: "Close Tab",
          disabled: !activePath,
          run: () => activePath && closeTab(activePath),
        },
      ],
    },
    {
      id: "edit",
      label: "Edit",
      items: [
        { kind: "item", label: "Undo", hint: `${MOD}Z`, run: () => runEditorCommand("undo", true) },
        {
          kind: "item",
          label: "Redo",
          hint: `${MOD}⇧Z`,
          run: () => runEditorCommand("redo", true),
        },
        { kind: "sep" },
        {
          kind: "item",
          label: "Cut",
          hint: `${MOD}X`,
          run: () => runEditorCommand("editor.action.clipboardCutAction"),
        },
        {
          kind: "item",
          label: "Copy",
          hint: `${MOD}C`,
          run: () => runEditorCommand("editor.action.clipboardCopyAction"),
        },
        {
          kind: "item",
          label: "Paste",
          hint: `${MOD}V`,
          run: () => runEditorCommand("editor.action.clipboardPasteAction"),
        },
        { kind: "sep" },
        {
          kind: "item",
          label: "Find",
          hint: `${MOD}F`,
          run: () => runEditorCommand("actions.find"),
        },
        {
          kind: "item",
          label: "Replace",
          hint: `${MOD}${ALT}F`,
          run: () => runEditorCommand("editor.action.startFindReplaceAction"),
        },
      ],
    },
    {
      id: "selection",
      label: "Selection",
      items: [
        {
          kind: "item",
          label: "Select All",
          hint: `${MOD}A`,
          run: () => runEditorCommand("editor.action.selectAll"),
        },
        {
          kind: "item",
          label: "Expand Selection",
          hint: `${MOD}⇧→`,
          run: () => runEditorCommand("editor.action.smartSelect.expand"),
        },
        {
          kind: "item",
          label: "Shrink Selection",
          hint: `${MOD}⇧←`,
          run: () => runEditorCommand("editor.action.smartSelect.shrink"),
        },
        { kind: "sep" },
        {
          kind: "item",
          label: "Add Cursor Below",
          hint: `${MOD}${ALT}↓`,
          run: () => runEditorCommand("editor.action.insertCursorBelow"),
        },
      ],
    },
    {
      id: "view",
      label: "View",
      items: [
        {
          kind: "item",
          label: "Command Palette…",
          hint: `${MOD}⇧P`,
          run: () => setCommandPalette(true),
        },
        { kind: "sep" },
        {
          kind: "item",
          label: sidebarVisible ? "Hide Sidebar" : "Show Sidebar",
          hint: `${MOD}B`,
          run: () => toggleSidebar(),
        },
        {
          kind: "item",
          label: bottomVisible ? "Hide Panel" : "Show Panel",
          hint: `${MOD}J`,
          run: () => toggleBottom(),
        },
        { kind: "sep" },
        { kind: "head", label: "Tool panels" },
        { kind: "item", label: "Problems", run: () => setBottomTab("problems") },
        { kind: "item", label: "Output", run: () => setBottomTab("output") },
        { kind: "item", label: "Script", run: () => setBottomTab("script") },
        { kind: "item", label: "Certification", run: () => setBottomTab("certification") },
        { kind: "item", label: "Cost", run: () => setBottomTab("cost") },
        { kind: "sep" },
        { kind: "head", label: "Theme" },
        ...THEMES.map(
          (t): MenuItem => ({
            kind: "item",
            label: t.label,
            checked: theme === t.id,
            run: () => setTheme(t.id),
          }),
        ),
        { kind: "sep" },
        { kind: "item", label: "Settings", run: () => setActivityView("settings") },
      ],
    },
    {
      id: "go",
      label: "Go",
      items: [
        {
          kind: "item",
          label: "Go to File…",
          hint: `${MOD}P`,
          run: () => setCommandPalette(true),
        },
        {
          kind: "item",
          label: "Go to Line…",
          hint: `${MOD}G`,
          run: () => runEditorCommand("editor.action.gotoLine"),
        },
        {
          kind: "item",
          label: "Go to Symbol…",
          run: () => runEditorCommand("editor.action.quickOutline"),
        },
      ],
    },
    {
      id: "run",
      label: "Run",
      items: [
        {
          kind: "item",
          label: "Compile to Address",
          hint: `${MOD}↵`,
          run: () => void runCompile("fund"),
        },
      ],
    },
    {
      id: "help",
      label: "Help",
      items: [
        {
          kind: "item",
          label: "Command Palette…",
          hint: `${MOD}⇧P`,
          run: () => setCommandPalette(true),
        },
        { kind: "sep" },
        { kind: "head", label: "Seal" },
        { kind: "item", label: "A verification language for Bitcoin tapscript", disabled: true, run: () => {} },
        {
          kind: "item",
          label: "Seal on GitHub",
          run: () => window.open("https://github.com/askii21m/seal", "_blank", "noopener,noreferrer"),
        },
      ],
    },
  ];

  const onMenuKey = (e: ReactKeyboardEvent, idx: number) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      setOpen(menus[(idx + 1) % menus.length].id);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      setOpen(menus[(idx - 1 + menus.length) % menus.length].id);
    } else if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(menus[idx].id);
    }
  };

  return (
    <div className="mb" ref={barRef} role="menubar" aria-label="Application menu">
      {menus.map((menu, idx) => (
        <div key={menu.id} className="mb-menu">
          <button
            type="button"
            className={`mb-trigger${open === menu.id ? " is-open" : ""}`}
            aria-haspopup="true"
            aria-expanded={open === menu.id}
            onClick={() => setOpen(open === menu.id ? null : menu.id)}
            onMouseEnter={() => open && setOpen(menu.id)}
            onKeyDown={(e) => onMenuKey(e, idx)}
          >
            {menu.label}
          </button>
          {open === menu.id && (
            <div className="mb-dropdown" role="menu" aria-label={menu.label}>
              {menu.items.map((item, i) => {
                if (item.kind === "sep") return <div key={i} className="mb-sep" role="separator" />;
                if (item.kind === "head")
                  return (
                    <div key={i} className="mb-head" aria-hidden="true">
                      {item.label}
                    </div>
                  );
                return (
                  <button
                    key={i}
                    type="button"
                    role="menuitem"
                    className="mb-item"
                    disabled={item.disabled}
                    onClick={() => run(item.run)}
                  >
                    <span className="mb-item-check" aria-hidden="true">
                      {item.checked && <Check size={13} strokeWidth={2.25} />}
                    </span>
                    <span className="mb-item-label">{item.label}</span>
                    {item.hint && <span className="mb-item-hint">{item.hint}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
